import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import CallsFilterPane from "./CallsFilterPane";
import CallsTable from "./CallsTable";
import {
  buildCallColumns,
  loadColumnPrefs,
  saveColumnPrefs,
  COLUMN_META,
} from "./callsTableConfig";
import useOrgRouteNames from "../../hooks/useOrgRouteNames";

/** Excel-friendly timestamp in org timezone: YYYY-MM-DD HH:MM:SS */
const formatExcelTimestamp = (timestamp) => {
  if (!timestamp || timestamp === "N/A") return "";
  let ts = typeof timestamp === "string" ? Number(timestamp) : timestamp;
  if (Number.isNaN(ts)) return "";
  if (String(ts).length === 10) {
    ts *= 1000;
  }
  const date = new Date(ts);
  if (isNaN(date.getTime())) return "";

  try {
    const org = JSON.parse(localStorage.getItem("organization") || "{}");
    const timeZone = org?.org_timezone?.S || "UTC";
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
  } catch {
    return date.toISOString().replace("T", " ").substring(0, 19);
  }
};

const CallsListPanel = ({
  calls,
  navigate,
  dateRange,
  onDatePresetChange,
  startDate,
  endDate,
  onCustomDateChange,
  isLoading,
}) => {
  const { resolveRouteName } = useOrgRouteNames();

  const columns = useMemo(() => buildCallColumns(), []);
  const initialPrefs = useMemo(() => loadColumnPrefs(), []);

  // Resolve route IDs → names for table cells + filters (keep raw `route` id on row)
  const callsWithRouteNames = useMemo(
    () =>
      (calls || []).map((call) => ({
        ...call,
        routeName: call.route ? resolveRouteName(call.route) : "",
      })),
    [calls, resolveRouteName]
  );

  const [columnVisibility, setColumnVisibility] = useState(
    initialPrefs.columnVisibility
  );
  const [columnOrder, setColumnOrder] = useState(initialPrefs.columnOrder);
  const [sorting, setSorting] = useState([
    { id: "callTime", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 12,
  });

  useEffect(() => {
    saveColumnPrefs(columnVisibility, columnOrder);
  }, [columnVisibility, columnOrder]);

  // Reset to first page when filters / data change
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [columnFilters, callsWithRouteNames]);

  const table = useReactTable({
    data: callsWithRouteNames,
    columns,
    state: {
      columnVisibility,
      columnOrder,
      sorting,
      columnFilters,
      pagination,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row, index) => row.callId || String(index),
  });

  const handleExport = () => {
    try {
      // Filtered + sorted rows (same set as the table, not just the current page)
      const rows = table.getSortedRowModel().rows.map((r) => r.original);
      if (!rows.length) {
        alert("No data to export");
        return;
      }

      // All displayable columns (default order) + full call link — independent of visibility
      const exportColumns = [
        ...COLUMN_META.map((col) => ({
          id: col.id,
          label: col.label,
          accessorKey: col.accessorKey,
        })),
        { id: "link", label: "Link", accessorKey: "link" },
      ];

      const headers = exportColumns.map((c) => c.label);

      const formatExportValue = (row, col) => {
        if (col.id === "link") {
          return row.link ? window.location.origin + row.link : "";
        }
        if (col.id === "callTime") {
          return formatExcelTimestamp(row.callTime);
        }
        const value = row[col.accessorKey];
        if (value == null || value === "N/A") return "";
        return value;
      };

      const csvRows = [
        headers.join(","),
        ...rows.map((row) =>
          exportColumns
            .map((col) => {
              const value = formatExportValue(row, col);
              return `"${String(value ?? "").replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ];

      const formatDateForFilename = (dateStr) =>
        dateStr
          ? String(dateStr).split("T")[0]
          : new Date().toISOString().split("T")[0];

      const filename = `twilanswer_calls_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.csv`;

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV export failed:", err);
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 pt-4 pb-1 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Recent Calls</h3>
        {startDate && endDate && (
          <span className="text-xs text-gray-500">
            {new Date(startDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {new Date(endDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        )}
      </div>

      <CallsFilterPane
        table={table}
        calls={callsWithRouteNames}
        dateRange={dateRange}
        onDatePresetChange={onDatePresetChange}
        startDate={startDate}
        endDate={endDate}
        onCustomDateChange={onCustomDateChange}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        onExport={handleExport}
      />

      <CallsTable
        table={table}
        isLoading={isLoading}
        onRowNavigate={(link) => navigate(link)}
      />
    </div>
  );
};

export default CallsListPanel;
